// src/hooks/useHideOnScroll.js
// Masque la barre du haut quand on descend, la ramène dès qu'on remonte (façon
// app native). Écoute en phase de capture : dans cette app ce n'est pas la
// fenêtre qui défile mais les conteneurs internes des pages (h-[100dvh]), et ils
// ne sont pas connus d'avance.
import { useEffect, useRef, useState } from "react";

const DELTA_MIN = 6; // px — ignore le tremblement du doigt
const TOP_ZONE = 24; // px — près du haut, la barre est toujours visible
// Masquer la barre rend sa hauteur au contenu : la page grandit, le conteneur
// interne bouge et émet un scroll… qui rebasculerait l'état, en boucle. On
// ignore donc les scrolls pendant que notre propre transition se joue.
const SETTLE_MS = 400;

export default function useHideOnScroll(enabled = true) {
  const [hidden, setHidden] = useState(false);
  // Un scrollTop par conteneur : plusieurs peuvent défiler (page, liste, chat).
  const lastTops = useRef(new WeakMap());
  const hiddenRef = useRef(false);
  const lockedUntil = useRef(0);

  useEffect(() => {
    if (!enabled) {
      hiddenRef.current = false;
      setHidden(false);
      return undefined;
    }

    const onScroll = (e) => {
      const el = e.target === document || e.target === window ? document.scrollingElement : e.target;
      if (!el || typeof el.scrollTop !== "number") return;

      const y = el.scrollTop;

      // Pendant le verrou, on se contente de resynchroniser la référence : le
      // scrollTop a pu sauter à cause du reflow, pas à cause du doigt.
      if (performance.now() < lockedUntil.current) {
        lastTops.current.set(el, y);
        return;
      }

      const prev = lastTops.current.get(el) ?? 0;
      const dy = y - prev;
      if (Math.abs(dy) < DELTA_MIN) return;
      lastTops.current.set(el, y);

      const next = y <= TOP_ZONE ? false : dy > 0;
      if (next === hiddenRef.current) return;

      hiddenRef.current = next;
      lockedUntil.current = performance.now() + SETTLE_MS;
      setHidden(next);
    };

    document.addEventListener("scroll", onScroll, { capture: true, passive: true });
    return () => document.removeEventListener("scroll", onScroll, { capture: true });
  }, [enabled]);

  return hidden;
}
